// src/pages/EntrepreneurshipEducationDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaTasks,
    FaUsers,
    FaCertificate,
    FaBuilding,
    FaFlag, 
    FaGlobeAfrica,
    FaGlobe,
    FaArrowLeft,
    FaGraduationCap, 
} from 'react-icons/fa';

// Import content pages
import ModuleContentPage from './EntrepreneurshipEducationContent/ModuleContentPage';
import AssessmentContentPage from './EntrepreneurshipEducationContent/AssessmentContentPage'; 
import GroupsContentPage from './EntrepreneurshipEducationContent/GroupsContentPage'; 
import CertificationsContentPage from './EntrepreneurshipEducationContent/CertificationsContentPage'; 

// Import the CSS file for this component.
import '../styles/EntrepreneurshipEducationDashboard.css'; 

const API_BASE = "";

// --- Scope Selection Overview (Initial Landing if no scope is set) ---
const ScopeSelectionOverview = ({ selectScope }) => {
  const scopeItems = [
    { id: 'Local', label: 'Local Entrepreneurship', icon: <FaBuilding size={60} className="text-teal-500 mb-4" />, description: "Focus on your immediate community and market." },
    { id: 'National', label: 'National Entrepreneurship', icon: <FaFlag size={60} className="text-indigo-500 mb-4" />, description: "Expand your reach within the country." },
    { id: 'Continental', label: 'Continental Entrepreneurship', icon: <FaGlobeAfrica size={60} className="text-orange-500 mb-4" />, description: "Explore opportunities across the continent." },
    { id: 'Global', label: 'Global Entrepreneurship', icon: <FaGlobe size={60} className="text-red-500 mb-4" />, description: "Take your vision to the world stage." },
  ];

  return (
    <div className="scope-selection-wrapper p-4 md:p-8"> 
      <div className="scope-selection-content-container w-full">
        <h2 className="text-4xl font-bold text-gray-800 mb-10 md:mb-12 text-center">Choose Your Entrepreneurship Arena</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-12 w-full max-w-4xl mx-auto">
          {scopeItems.map((item) => (
            <button
              key={item.id}
              onClick={() => selectScope(item.id)}
              className="bg-white p-10 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 ease-in-out flex flex-col items-center text-center transform hover:-translate-y-2 hover:scale-105"
            >
              {item.icon}
              <h3 className="text-2xl font-semibold text-gray-700 mb-3">{item.label}</h3>
              <p className="text-gray-600 text-base">{item.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
// --- End of Scope Selection Overview ---


const EntrepreneurshipEducationDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('scopeSelectionOverview'); 
  const [currentScope, setCurrentScope] = useState(null); 

  // New state for fetched dashboard data and loading/error states
  const [dashboardData, setDashboardData] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);

  useEffect(() => {
    // --- Authentication Check ---
    const paymentStatus = localStorage.getItem("paymentStatus");
    const paidPlan = JSON.parse(localStorage.getItem("paidPlan"));
    if (process.env.NODE_ENV === 'development' && window.location.pathname.startsWith("/dev-")) {
      console.log("DEVELOPMENT ACCESS: Bypassing payment and plan checks for EntrepreneurshipEducationDashboard.");
      return;
    }

    if (paymentStatus !== "success" || !paidPlan || !paidPlan.features.includes("Entrepreneurship")) {
      navigate("/checkout");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentScope) return;

      setIsLoadingData(true);
      setDataError(null);
      setDashboardData({});

      const endpoints = {
        'learningContent': `/api/dashboard/entrepreneurship/modules?scope=${currentScope.toLowerCase()}`,
        'assessments': `/api/dashboard/entrepreneurship/assessments?scope=${currentScope.toLowerCase()}`,
        'groups': `/api/dashboard/entrepreneurship/groups?scope=${currentScope.toLowerCase()}`,
        'certifications': `/api/dashboard/entrepreneurship/certifications?scope=${currentScope.toLowerCase()}`
      };

      try {
        const response = await fetch(`${API_BASE}${endpoints[activeView]}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setDashboardData(data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setDataError("Failed to load dashboard content. Please try again.");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchDashboardData();
  }, [currentScope, activeView]);
  
  const selectScopeAndGoToLearning = (scopeId) => {
    setCurrentScope(scopeId);
    setActiveView('learningContent'); 
  };

  const navigateToDashboardView = (viewId, scope = null) => {
    if (scope && scope !== currentScope) { 
        setCurrentScope(scope);
        setActiveView('learningContent'); 
        return;
    }
    if (viewId === 'scopeSelectionOverview'){
        setCurrentScope(null); 
    } else if (viewId !== 'functionalCardOverview' && !currentScope && !scope) {
        setActiveView('scopeSelectionOverview');
        return;
    }
    setActiveView(viewId);
  };

  // Define functionalMenuItems for the HORIZONTAL menu in the main content area
  const horizontalFunctionalMenuItems = [
    { id: 'learningContent', label: 'Learning', icon: <FaGraduationCap /> },
    { id: 'assessments', label: 'Assessments', icon: <FaTasks /> },
    { id: 'groups', label: 'Groups', icon: <FaUsers /> },
    { id: 'certifications', label: 'Certs', icon: <FaCertificate /> },
  ];

  const renderActiveViewDetail = () => {
    if (!currentScope) { 
        return <ScopeSelectionOverview selectScope={selectScopeAndGoToLearning} />;
    }
    
    if (isLoadingData) {
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <p>Loading...</p> {/* Replaced FaSpinner to resolve build error */}
        </div>
      );
    }

    if (dataError) {
      return (
        <div className="flex items-center justify-center min-h-[50vh]">
          <p className="text-red-500 text-lg">{dataError}</p>
        </div>
      );
    }
    
    switch (activeView) {
      case 'learningContent':
        return <ModuleContentPage currentScope={currentScope} navigateToDashboardView={navigateToDashboardView} modules={dashboardData} />;
      case 'assessments':
        return <AssessmentContentPage currentScope={currentScope} navigateToDashboardView={navigateToDashboardView} assessments={dashboardData} />;
      case 'groups':
        return <GroupsContentPage currentScope={currentScope} navigateToDashboardView={navigateToDashboardView} groups={dashboardData} />;
      case 'certifications':
        return <CertificationsContentPage currentScope={currentScope} navigateToDashboardView={navigateToDashboardView} certifications={dashboardData} />;
      default: 
        return <ModuleContentPage currentScope={currentScope} navigateToDashboardView={navigateToDashboardView} modules={dashboardData} />;
    }
  };
  
  const isSidebarVisible = activeView !== 'scopeSelectionOverview' && currentScope;
  
  // This is for the small functional menu INSIDE the sidebar
  const functionalMenuItemsForSidebar = horizontalFunctionalMenuItems.map(item => ({ // Based on the same items
    ...item,
    icon: React.cloneElement(item.icon, { className: "mr-1 md:mr-2" }) 
  }));

  return (
    <div className="flex min-h-screen bg-gray-100 font-sans">
      {isSidebarVisible && ( 
        <aside className="w-64 bg-gradient-to-b from-blue-700 to-blue-900 text-white p-4 shadow-lg fixed top-0 left-0 h-full pt-[calc(env(safe-area-inset-top)_+_82px)] flex flex-col">
          <button 
              onClick={() => {
                  setCurrentScope(null); 
                  setActiveView('scopeSelectionOverview'); 
              }}
              className="w-full text-left py-2 px-3 mb-4 rounded-md text-sm hover:bg-blue-600 flex items-center"
          >
            <FaArrowLeft className="mr-2" /> Choose Arena
          </button>
          
          <div className="mt-auto"> 
            <h2 className="text-md font-semibold mb-3 text-blue-300 text-center border-t border-blue-600 pt-3">
              {currentScope} Menu:
            </h2>
            <nav className="flex flex-row flex-wrap justify-around gap-3 p-1"> 
              {functionalMenuItemsForSidebar.map(funcItem => ( 
                  <button
                    key={funcItem.id}
                    onClick={() => setActiveView(funcItem.id)}
                    title={funcItem.label}
                    className={`flex flex-col items-center justify-center p-1.5 rounded-lg transition-colors duration-150 ease-in-out text-xs w-[calc(25%_-_0.75rem)] h-14 max-w-[55px] 
                        ${activeView === funcItem.id
                        ? 'bg-sky-500 text-white font-medium'
                        : 'hover:bg-sky-600 hover:text-white'
                        }`}
                  >
                    {funcItem.icon}
                    <span className="mt-1 text-center truncate w-full text-[10px]">{funcItem.label}</span> 
                  </button>
              ))}
            </nav>
          </div>
        </aside>
      )}

      <main className={`flex-1 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {activeView === 'scopeSelectionOverview' || !currentScope ? 
            <ScopeSelectionOverview selectScope={selectScopeAndGoToLearning} /> 
            : 
            <div className="p-0"> 
                {/* Horizontal Functional Menu for the currentScope */}
                {/* This menu is shown when a scope is selected and we are not on the main scope overview */}
                {/* Increased mb-20 to mb-32 for more space below this menu, for debugging visibility */}
                <div className="bg-white shadow-md rounded-lg mb-32 p-3 mx-4 mt-4 md:mx-6 md:mt-6"> 
                    <nav className="flex flex-row flex-wrap justify-center items-center gap-4 md:gap-6"> 
                        {horizontalFunctionalMenuItems.map(funcItem => ( 
                            <button
                              key={funcItem.id}
                              onClick={() => setActiveView(funcItem.id)}
                              title={funcItem.label}
                              className={`flex items-center py-2 px-4 rounded-md transition-colors duration-150 ease-in-out text-sm font-medium
                                  ${activeView === funcItem.id
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                            >
                              {React.cloneElement(funcItem.icon, { className: "mr-2 text-lg" })}
                              <span>{funcItem.label}</span> 
                            </button>
                        ))}
                    </nav>
                </div>
                {/* Render the detailed content */}
                {/* The padding for the detail view content itself is handled by this div */}
                <div className="p-4 md:p-6"> 
                    {renderActiveViewDetail()}
                </div>
            </div>
        }
      </main>
    </div>
  );
};

export default EntrepreneurshipEducationDashboard;
